// src/pages/Product/ProductPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '../../hooks/useCart.js';
import { useFavorites } from '../../hooks/useFavorites.js';
import { useToast } from '../../hooks/useToast.js';
import { Button } from '../../components/ui/Button/Button.jsx';
import { productsAPI } from '../../api/products.js';
import { reviewsAPI } from '../../api/reviews.js';
import { norranAPI } from '../../api/norran.js';

export const ProductPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const containerRef = useRef(null);

  // ⭐ ЦЕНА ИЗ NORRAN
  //
  // 🔴 NORRAN В ПРИОРИТЕТЕ. Решение владельца: если расчёт есть — показываем
  //    ЕГО цену, а статичную `product.price` на карточке НЕ показываем
  //    вовсе. Иначе на странице оказались бы два разных числа
  //    (например, 1500 ₽ из БД против 3268 ₽ из NORRAN) и клиент
  //    не понял бы, какому верить.
  //
  // ⚠️ Источник истины по деньгам — NORRAN (NORRAN_INTEGRATION.txt §0).
  //    `product.price` остаётся только запасным вариантом на случай,
  //    когда расчёта нет или NORRAN недоступен.
  const [norranPrice, setNorranPrice] = useState(null);
  // Ссылка на расчёт: `calculatorId` + `calculatorVersionId`.
  // Без версии расчёт невоспроизводим — состав операций в NORRAN может
  // измениться после того, как клиент посчитал.
  const [norranMeta, setNorranMeta] = useState({});
  const [norranState, setNorranState] = useState('idle'); // idle|loading|ok|unavailable

  const { addToCart } = useCart();
  const { addToFavorites, removeFromFavorites } = useFavorites();
  const { success, error } = useToast();

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    setIsLoading(true);
    try {
      const [productRes, relatedRes, reviewsRes] = await Promise.all([
        productsAPI.getById(id),
        productsAPI.getRelated(id),
        reviewsAPI.getByProduct(id, { limit: 5 }),
      ]);
      setProduct(productRes.data.data);
      setRelatedProducts(relatedRes.data.data || []);
      setReviews(reviewsRes.data.data.reviews || []);
    } catch (err) {
      error('Не удалось загрузить товар');
    } finally {
      setIsLoading(false);
    }
  };

  // ⭐ ЗАГРУЗКА ВИДЖЕТА КАЛЬКУЛЯТОРА NORRAN ЧЕРЕЗ useEffect
  //
  // 🔴 ЧТО БЫЛО СЛОМАНО (исправлено 2026-10-07):
  //    iframe.src был ЗАХАРДКОЖЕН:
  //      "https://whatproduct.ru/calculator/00000000-0000-0000-0000-000000000000/bukvy_iz_penoplasta?embed=true"
  //    Три независимых дефекта в одной строке:
  //      1. `whatproduct.ru` — домен МЁРТВ (не отвечает вовсе). Калькулятор
  //         не работал НИ НА ОДНОМ товаре.
  //      2. UUID `00000000-…` — фейковая заглушка.
  //      3. Код `bukvy_iz_penoplasta` подставлялся ВСЕМ товарам подряд —
  //         даже «Световым буквам».
  //
  // 🔑 ТЕПЕРЬ АДРЕС СОБИРАЕТСЯ ИЗ ДАННЫХ:
  //      база  — `VITE_NORRAN_BASE_URL` (на проде https://norran.ru);
  //      код   — `product.norran_calculator_code` (поле в БД Студии);
  //      компания — `VITE_NORRAN_COMPANY` (slug или UUID).
  //    Формат NORRAN: `/calculator/<companySlug>/<calculatorCode>`.
  //
  //    ⚠️ ПОЧЕМУ `norran_calculator_code`, А НЕ `calculator_id`.
  //    `calculator_id` указывает на ЛОКАЛЬНУЮ таблицу `calculators` Студии,
  //    где лежат мёртвые записи (`LettersCalculator` — компонента с таким
  //    именем в коде НЕТ). Для NORRAN это поле бесполезно: там СВОИ
  //    идентификаторы. Разбор — в миграции
  //    `2026-10-07-norran-calculator-mapping.sql`.
  useEffect(() => {
    const code = product?.norran_calculator_code;
    // 🔑 ВОРОТА ИСТОЧНИКА ЦЕНЫ — те же, что и у запроса цены выше.
    //    Iframe с калькулятором NORRAN показывается ТОЛЬКО для товаров
    //    с `price_source === 'norran'`. Иначе гость увидел бы чужой
    //    интерфейс NORRAN на странице обычного товара с фиксированной ценой.
    //    По умолчанию ('fixed') калькулятора нет — это НОРМА.
    if ((product?.price_source || 'fixed') !== 'norran' || !code) return;

    const baseUrl = import.meta.env.VITE_NORRAN_BASE_URL;
    const company = import.meta.env.VITE_NORRAN_COMPANY;

    // ⚠️ Если конфигурации нет — НЕ строим ссылку «наугад» и НЕ показываем
    //    пустой iframe. Раньше в этом случае грузился мёртвый домен, и
    //    пользователь видел белую рамку вместо калькулятора.
    if (!baseUrl || !company) {
      console.warn(
        '[Calculator] Не заданы VITE_NORRAN_BASE_URL / VITE_NORRAN_COMPANY — ' +
        'калькулятор не будет показан. См. frontend/.env.example'
      );
      return;
    }

    if (containerRef.current) {
      containerRef.current.innerHTML = '';

      const iframe = document.createElement('iframe');
      iframe.src = `${baseUrl}/calculator/${company}/${code}?embed=true`;
      iframe.style.width = '100%';
      iframe.style.border = 'none';
      iframe.style.minHeight = '600px';
      iframe.style.height = 'auto';
      iframe.allow = 'fullscreen';
      iframe.title = 'Калькулятор стоимости';
      iframe.className = 'calculator-iframe';
      containerRef.current.appendChild(iframe);

      const handleMessage = (e) => {
        if (e.data && e.data.type === 'resize-calculator') {
          iframe.style.height = e.data.height + 'px';
        }
      };

      window.addEventListener('message', handleMessage);

      return () => {
        window.removeEventListener('message', handleMessage);
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }
      };
    }
  }, [product]);

  // ⭐ ЗАПРОС ЦЕНЫ У NORRAN (через backend Студии)
  //
  // 🔴 ЗАЧЕМ DEBOUNCE. У NORRAN лимит 100 запросов в МИНУТУ на IP
  //    (замерено по заголовкам, см. NORRAN_REST_API.txt §6). В реальном
  //    калькуляторе ползунок шлёт запрос на каждое движение; без паузы
  //    активный посетитель выбирает лимит и получает 429 — причём
  //    «общий бакет», если перед NORRAN стоит прокси без TRUST_PROXY.
  //    Здесь запрос один на товар, но пауза всё равно нужна: React
  //    в dev-режиме монтирует компонент дважды (StrictMode).
  //
  // ⚠️ ЗАВИСИМОСТЬ ТОЛЬКО ОТ product. Если добавить сюда `norranPrice`,
  //    эффект зациклится: setNorranPrice меняет состояние → эффект
  //    срабатывает снова → новый запрос. Это классическая ловушка React.
  useEffect(() => {
    // 🔑 ВОРОТА ИСТОЧНИКА ЦЕНЫ (решение владельца 2026-10-10).
    //
    //    NORRAN — ДОПОЛНИТЕЛЬНЫЙ помощник, а не фундамент. Сайт обязан
    //    работать полностью автономно. Поэтому запрос цены в NORRAN
    //    делается ТОЛЬКО если товар явно помечен `price_source === 'norran'`.
    //
    //    Раньше условием был один лишь непустой `norran_calculator_code`:
    //    любой товар с кодом автоматически тянул цену из NORRAN, и без
    //    NORRAN витрина зависела от внешнего сервиса. Теперь по умолчанию
    //    ('fixed') цена берётся из БД и никаких внешних вызовов нет.
    const code = product?.norran_calculator_code;
    const source = product?.price_source || 'fixed';

    if (source !== 'norran' || !code) {
      // Цена фиксированная — берётся из `products.price`. Это НОРМА
      // и режим по умолчанию для всех товаров.
      setNorranPrice(null);
      setNorranState('idle');
      return;
    }

    let cancelled = false;
    setNorranState('loading');

    const timer = setTimeout(async () => {
      try {
        const res = await norranAPI.calculate(code, {});
        // ⚠️ Проверяем `cancelled` ПОСЛЕ await: пользователь мог уйти
        //    на другой товар, и ответ для старого не должен перезаписать
        //    цену нового.
        if (cancelled) return;
        setNorranPrice(res.data?.data?.totalPrice ?? null);
        setNorranMeta({
          calculatorId: res.data?.data?.calculatorId ?? null,
          calculatorVersionId: res.data?.data?.calculatorVersionId ?? null,
          calculatedAt: res.data?.data?.calculatedAt ?? null,
        });
        setNorranState('ok');
      } catch (err) {
        if (cancelled) return;
        // 🔴 НЕ показываем ошибку тостом и НЕ роняем страницу.
        //    Требование отказоустойчивости: Студия обязана работать,
        //    даже когда NORRAN лежит (NORRAN_INTEGRATION.txt §1).
        //    Просто откатываемся на цену из БД.
        //    422 = расчёт для изделия не настроен (нет операций);
        //    503 = NORRAN недоступен; 429 = лимит запросов.
        setNorranPrice(null);
        setNorranMeta({});
        setNorranState('unavailable');
        console.warn(
          `[Norran] Цена для «${code}» недоступна:`,
          err.response?.data?.reason || err.message
        );
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [product]);

  const handleAddToCart = () => {
    if (!product) return;

    // ⭐ СОХРАНЯЕМ ССЫЛКУ НА РАСЧЁТ NORRAN В КОРЗИНЕ → ЗАКАЗЕ.
    //
    // 🔴 ЗАЧЕМ. Считать в калькуляторе и заказать в Студии — это ДВА
    //    разных следа, если их не связать (NORRAN_INTEGRATION.txt §5).
    //    Мы сохраняем ССЫЛКУ на расчёт, а НЕ копию расчёта: копия была бы
    //    второй производственной моделью, а это главный запрет проекта.
    //
    // 🔑 КУДА ОНО ИДЁТ (цепочка уже существует, я её не создаю —
    //    она просто не была заполнена):
    //      этот объект → cartAPI.add → `cart_items.calculator_data`
    //      → Checkout.jsx:67 → `order_items.calculator_data`
    //      → видно в Cart.jsx:117 и AdminOrderDetail.jsx:182.
    //
    // ⚠️ `calculatorVersionId` ОБЯЗАТЕЛЕН для воспроизводимости: в NORRAN
    //    калькулятор версионируется, и без версии расчёт не повторить —
    //    состав операций мог измениться после того, как клиент посчитал.
    const calculatorData = norranPrice !== null
      ? {
          source: 'norran',
          // ⚠️ Из переменной окружения, а НЕ строкой: слэг компании уже
          //    задан в `frontend/.env` для iframe, и дублировать его
          //    литералом — значит завести вторую правду, которая
          //    разъедется при смене компании.
          company: import.meta.env.VITE_NORRAN_COMPANY || null,
          calculatorCode: product.norran_calculator_code,
          calculatorId: norranMeta.calculatorId,
          calculatorVersionId: norranMeta.calculatorVersionId,
          totalPrice: norranPrice,
          calculatedAt: norranMeta.calculatedAt,
        }
      : {};

    addToCart({
      product_id: product.id,
      quantity: 1,
      calculator_data: calculatorData,
    });
    success('Товар добавлен в корзину');
  };

  const handleToggleFavorite = async () => {
    if (!product) return;
    try {
      if (isLiked) {
        await removeFromFavorites(product.id);
        setIsLiked(false);
        success('Удалено из избранного');
      } else {
        await addToFavorites(product.id);
        setIsLiked(true);
        success('Добавлено в избранное');
      }
    } catch (err) {
      error('Не удалось обновить избранное');
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center font-[Inter,Manrope,Arial,sans-serif] text-sm text-muted">
        Загрузка...
      </div>
    );
  }

  if (!product) {
    return (
      <div className="py-20 text-center font-[Inter,Manrope,Arial,sans-serif]">
        <h2 className="text-2xl font-light text-content mb-4">Товар не найден</h2>
        <Link to="/catalog">
          <Button>Вернуться в каталог</Button>
        </Link>
      </div>
    );
  }

  const images = product.ProductImages || [];
  const mainImage = images.find(img => img.is_main) || images[0];

  return (
    <div className="bg-bg min-h-screen pb-16 font-[Inter,Manrope,Arial,sans-serif] text-content">
      
      {/* Breadcrumbs */}
      <nav className="mx-auto max-w-[1240px] px-4 pt-6 pb-4 text-xs font-light text-muted">
        <Link to="/" className="transition-colors hover:text-content">Главная</Link>
        <span className="mx-2 text-line-strong">/</span>
        <Link to="/catalog" className="transition-colors hover:text-content">Каталог</Link>
        <span className="mx-2 text-line-strong">/</span>
        <span className="font-medium text-content">{product.name}</span>
      </nav>

      {/* Main Product Info */}
      <div className="mx-auto max-w-[1240px] px-4">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 mb-16">
          
          {/* Gallery */}
          <div>
            <div className="mb-3 flex h-[420px] items-center justify-center overflow-hidden rounded-2xl border border-line-strong bg-surface">
              {mainImage ? (
                <span className="text-xs text-muted">Фото товара</span>
              ) : (
                <span className="text-xs text-muted">Нет изображения</span>
              )}
            </div>
            
            <div className="flex gap-2 overflow-x-auto pb-1">
              {[1, 2, 3, 4].map((_, i) => (
                <div
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`flex h-20 w-20 shrink-0 cursor-pointer items-center justify-center rounded-xl border bg-surface transition-all ${
                    activeImage === i ? 'border-accent shadow-sm' : 'border-line-strong hover:border-line-strong'
                  }`}
                >
                  <span className="text-[10px] text-muted">Фото {i + 1}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Info */}
          <div>
            <div className="mb-1 text-[10px] font-normal uppercase tracking-[.2em] text-accent">
              Товар каталога
            </div>
            
            <h1 className="text-3xl font-light tracking-tight text-content sm:text-4xl mb-3">
              {product.name}
            </h1>

            <div className="flex items-center gap-3 mb-4">
              <div className="flex gap-0.5 text-accent">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span key={star} className={`text-sm ${star <= 4 ? 'text-accent' : 'text-inactive'}`}>★</span>
                ))}
              </div>
              <span className="text-xs font-medium text-content">4.7</span>
              <span className="text-xs font-light text-muted">(12 отзывов)</span>
            </div>

            <div className="mb-6 rounded-xl border border-line-strong bg-surface p-4">
              <span className="text-xs font-light text-muted">Артикул: {product.sku || 'Н/Д'}</span>
              {/* 🔴 ПРИОРИТЕТ ЦЕНЫ: NORRAN → БД.
                  Если расчёт есть — показываем ТОЛЬКО его, статичную
                  цену НЕ дублируем (решение владельца: два разных числа
                  на карточке путают клиента). */}
              {norranPrice !== null ? (
                <>
                  <div className="mt-1 text-3xl font-light tracking-tight text-accent">
                    {norranPrice} ₽
                  </div>
                  <span className="text-xs font-light text-muted">
                    Расчёт по вашим параметрам
                  </span>
                </>
              ) : norranState === 'loading' ? (
                <div className="mt-1 text-3xl font-light tracking-tight text-line-strong">
                  Считаем…
                </div>
              ) : norranState === 'unavailable' && product.norran_calculator_code ? (
                // ⚠️ Честно: расчёт есть в теории, но получить его не удалось.
                //    НЕ показываем молча старую цену как «настоящую» — клиент
                //    должен понимать, что итог считает калькулятор ниже.
                <>
                  <div className="mt-1 text-3xl font-light tracking-tight text-accent">
                    {product.price ? `от ${product.price} ₽` : 'По запросу'}
                  </div>
                  <span className="text-xs font-light text-muted">
                    Точную стоимость рассчитает калькулятор ниже
                  </span>
                </>
              ) : (
                <div className="mt-1 text-3xl font-light tracking-tight text-accent">
                  {product.price ? `${product.price} ₽` : 'По запросу'}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-3 mb-6">
              <Button onClick={handleAddToCart} className="rounded-lg bg-accent px-6 py-2.5 text-xs font-medium text-white transition hover:bg-accent-hover">
                🛒 В корзину
              </Button>
              <Button
                variant="secondary"
                onClick={handleToggleFavorite}
                className={`rounded-lg border px-5 py-2.5 text-xs font-medium transition ${
                  isLiked 
                    ? 'border-accent bg-accent-soft text-accent' 
                    : 'border-line-strong bg-surface text-content hover:border-line-strong'
                }`}
              >
                {isLiked ? '❤️ В избранном' : '🤍 В избранное'}
              </Button>
            </div>

            <div className="mb-6 border-t border-line pt-5">
              <h3 className="text-sm font-medium uppercase tracking-wider text-content mb-2">Описание</h3>
              <p className="text-xs font-light leading-relaxed text-muted">
                {product.full_description || product.short_description || 'Описание отсутствует'}
              </p>
            </div>

            {product.ProductDetails && product.ProductDetails.length > 0 && (
              <div className="border-t border-line pt-5">
                <h3 className="text-sm font-medium uppercase tracking-wider text-content mb-3">
                  Характеристики
                </h3>
                <ul className="flex flex-col gap-2">
                  {product.ProductDetails.map((detail, i) => (
                    <li
                      key={i}
                      className="flex justify-between border-b border-surface-2 pb-2 text-xs font-light text-muted"
                    >
                      <span className="font-medium text-content">{detail.key}</span>
                      <span>{detail.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* ⭐ КАЛЬКУЛЯТОР NORRAN — ВИДЖЕТ ЧЕРЕЗ IFRAME ⭐
            ⚠️ Условие — по `norran_calculator_code`, а НЕ по `calculator_id`:
            последний ссылается на мёртвый локальный механизм, и блок
            показывался бы с пустой рамкой (см. разбор в useEffect выше). */}
        {product.norran_calculator_code && (
          <div className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div>
                <div className="mb-1 text-[10px] font-normal uppercase tracking-[.2em] text-accent">
                  Интерактив
                </div>
                <h2 className="text-2xl font-light tracking-tight text-content sm:text-3xl mb-2">
                  Рассчитайте стоимость
                </h2>
                <p className="text-xs font-light text-muted">
                  Укажите необходимые параметры и получите точный расчет под ваш проект
                </p>
              </div>
              
              <div className="overflow-hidden rounded-2xl border border-line-strong bg-surface shadow-sm min-h-[600px]">
                <div 
                  id="oko-calculator-container" 
                  ref={containerRef}
                  className="w-full min-h-[600px]"
                />
              </div>
            </div>
          </div>
        )}

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mb-16">
            <div className="mb-6 border-b border-line pb-3">
              <h2 className="text-xl font-light tracking-tight text-content sm:text-2xl">
                Похожие товары
              </h2>
            </div>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {relatedProducts.map((item) => (
                <Link
                  key={item.id}
                  to={`/product/${item.id}`}
                  className="group flex flex-col overflow-hidden rounded-xl border border-line-strong bg-surface transition-all hover:border-accent-hover hover:shadow-[0_10px_30px_rgba(0,0,0,0.06)]"
                >
                  <div className="aspect-[4/3] w-full flex items-center justify-center bg-bg text-xs text-muted">
                    Изображение
                  </div>
                  <div className="flex flex-1 flex-col justify-between border-t border-surface-2 p-4">
                    <h4 className="text-xs font-medium text-content transition-colors group-hover:text-accent-hover mb-2">
                      {item.name}
                    </h4>
                    <span className="text-xs font-medium text-accent">
                      {item.price ? `${item.price} ₽` : 'По запросу'}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Reviews */}
        <div>
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4 border-b border-line pb-4">
            <div>
              <div className="mb-1 text-[10px] font-normal uppercase tracking-[.2em] text-accent">
                Мнения
              </div>
              <h2 className="text-2xl font-light tracking-tight text-content sm:text-3xl">
                Отзывы ({reviews.length})
              </h2>
            </div>
            <Button variant="secondary" size="sm" className="rounded-lg border border-line-strong bg-surface px-4 py-2 text-xs font-medium text-content transition hover:border-accent-hover hover:text-accent-hover">
              Оставить отзыв
            </Button>
          </div>

          {reviews.length === 0 ? (
            <div className="py-10 text-center text-xs font-light text-muted">
              Пока нет отзывов. Будьте первым!
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="rounded-xl border border-line-strong bg-surface p-5 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-medium text-content">
                        {review.User?.first_name || review.guest_name || 'Аноним'}
                      </span>
                      <div className="flex gap-0.5 text-accent">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span key={star} className={`text-xs ${star <= review.rating ? 'text-accent' : 'text-inactive'}`}>★</span>
                        ))}
                      </div>
                    </div>
                    <span className="text-[10px] font-light text-subtle">
                      {new Date(review.createdAt).toLocaleDateString('ru-RU')}
                    </span>
                  </div>
                  <p className="text-xs font-light leading-relaxed text-muted">
                    {review.content}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default ProductPage;